import {
    type PrettyFn,
    type PrettyLogger,
    createPrettyLogger,
    type Pretty,
} from '@precon-tools-suite-by-shared/pretty-console';
import type { PrettyColor } from '@precon-tools-suite-by-shared/pretty-console';
import type { Component } from 'vue';
import { ComponentNotDefinedError } from '../errors';

interface StyleOptions {
    color?: PrettyColor;
    title?: string;
    titlePlural?: string;
    pathPrefix?: string;
}

function createStyles({
    color = 'teal',
    title = 'Partial',
    titlePlural,
    pathPrefix,
}: StyleOptions = {}) {
    titlePlural ??= title + 's';

    const pSubLabel =
        (value: string): PrettyFn =>
        ($) =>
            $(value, $.pos(color));
    const pLabel =
        (value: string, subLabel?: string): PrettyFn =>
        ($) => {
            const l = $(value, $.neg(color), $.upper);
            if (!subLabel) return l;
            return l.p(pSubLabel(subLabel));
        };
    const pTitle = (subLabel?: string) => pLabel(title, subLabel);
    const pTitlePlural = (subLabel?: string) =>
        pLabel(titlePlural as string, subLabel);
    const pName =
        (name: string): PrettyFn =>
        ($) =>
            $.p(pTitle())(name, $.pos(color), $.mono);
    const pPath =
        (name?: string): PrettyFn =>
        ($) => {
            const res = $(
                pathPrefix ?? '',
                $.fg(color, 'light'),
                $.italic,
                $.mono
            );
            if (!name) return res;
            return res(name, $.fg(color, 'dark'), $.mono);
        };
    const pFallback =
        (fallback: string | Component): PrettyFn =>
        ($) =>
            typeof fallback === 'string'
                ? pName(fallback)
                : $('[fallback-component]', $.bold, $.dim);

    return { pSubLabel, pFallback, pLabel, pTitle, pTitlePlural, pName, pPath };
}

export interface LoggerOptions extends StyleOptions {
    debugMode?: boolean;
    logger?: PrettyLogger;
}

export default function createLogger(options: LoggerOptions = {}) {
    const debugMode = !!options.debugMode;
    const logger = options.logger ?? console.pretty ?? createPrettyLogger();
    const styles = createStyles(options);
    const { pFallback, pName } = styles;

    function logLoadingFallback(name: string, fallback: string | Component) {
        if (debugMode)
            logger.info`Loading ${pFallback} instead of ${pName(
                name
            )}, which is not defined.`(fallback);
    }

    function logLoadingAttemptFailed(err: any, name: string, attempts: number) {
        if (debugMode)
            logger.log`Error "${err}" while loading ${pName(
                name
            )} at attempt ${attempts}.`();
    }

    function logLoadingFailed(err: any, name: string, attempts: number) {
        logger.error`Failed to load ${pName(
            name
        )} after ${attempts} attempts, with error ${err}`();
    }

    function logLazyLoading(name: string) {
        if (debugMode) logger.log`Lazy-loading the ${pName(name)}-module.`();
    }

    function makeNotDefinedError(name: string, ...args: any[]) {
        return Object.assign(
            new ComponentNotDefinedError(
                options.title ?? 'Partial',
                name,
                ...args
            ),
            {
                __pretty: ($) =>
                    $.s('Tried to load ')
                        .p(pName(name))
                        .s(', which is not defined'),
            } as Pretty
        );
    }

    return {
        ...styles,
        debugMode,
        logger,
        logLoadingFallback,
        logLoadingAttemptFailed,
        logLoadingFailed,
        logLazyLoading,
        makeNotDefinedError,
    };
}
